import React from 'react';
import { Wallet, TrendingUp, Percent, CreditCard, PiggyBank, AlertCircle, Shield } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета налогов
 */
const ResultsOverview = ({ results, formData }) => {
  if (!results) return null;

  // Определение типа налоговой системы для отображения
  const getTaxSystemName = (taxSystem) => {
    switch (taxSystem) {
      case 'npd':
        return 'Налог на профессиональный доход (самозанятость)';
      case 'usn_income':
        return 'УСН Доходы (6%)';
      case 'usn_profit':
        return 'УСН Доходы минус расходы (15%)';
      default:
        return 'Неизвестная система';
    }
  };

  // Определение источника дохода
  const getIncomeTypeName = (incomeType) => {
    switch (incomeType) {
      case 'general':
        return 'От физических лиц';
      case 'ip_insurance':
        return 'От юридических лиц и ИП';
      default:
        return 'Смешанный';
    }
  };

  // Определение периода дохода
  const getIncomePeriodName = (incomePeriod) => {
    switch (incomePeriod) {
      case 'month':
        return 'Месяц';
      case 'quarter':
        return 'Квартал';
      case 'year':
        return 'Год';
      case 'custom':
        return 'Произвольный период';
      default:
        return 'Неизвестный период';
    }
  };

  return (
    <ToolResult title="Результаты расчета налогов">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Сумма налога */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <Wallet className="w-4 h-4"/>
            Сумма налога ({results.taxSystem === 'npd' ? 'НПД' : results.taxSystem === 'usn_income' ? 'УСН' : 'УСН Д-Р'})
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatCurrency(results.calculatedTax)}
            </span>
            <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
              ({formatNumber(results.taxRate, 1)}% от базы)
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            {results.taxSystem === 'npd' 
              ? 'Фиксированная ставка для самозанятых' 
              : results.taxSystem === 'usn_income'
                ? 'Налог УСН Доходы 6%'
                : 'Налог УСН Доходы минус расходы 15%'}
          </p>
        </div>

        {/* Налоговая нагрузка */}
        <div className="rounded-lg bg-gradient-to-br from-rose-50 to-orange-50 dark:from-rose-900/30 dark:to-orange-900/30 p-5 border border-rose-100 dark:border-rose-800/50 shadow-sm">
          <h3 className="text-base font-medium text-rose-700 dark:text-rose-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            Общая налоговая нагрузка
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-rose-800 dark:text-rose-200" aria-live="polite">
              {formatCurrency(results.totalTaxBurden)}
            </span>
            <span className="text-sm font-medium text-rose-600 dark:text-rose-400">
              ({formatNumber(results.effectiveTaxRate, 1)}% от дохода)
            </span>
          </div>
          <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">
            Включает налоги{formData.fiscalRole === 'entrepreneur' ? ' и страховые взносы' : ''}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Доход" 
          value={formatCurrency(results.incomeAmount)} 
          color="indigo" 
          icon={<CreditCard className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Период" 
          value={getIncomePeriodName(formData.incomePeriod)} 
          color="gray" 
        />

        <ResultItem 
          label="Тип дохода" 
          value={getIncomeTypeName(formData.incomeType)} 
        />
        
        <ResultItem 
          label="Налоговая система" 
          value={getTaxSystemName(formData.taxSystem)} 
          color="blue" 
          icon={<Shield className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Статус" 
          value={formData.fiscalRole === 'individual' ? 'Физическое лицо' : 'Индивидуальный предприниматель'} 
        />
        
        <ResultItem 
          label="Налоговая ставка" 
          value={`${formatNumber(results.taxRate, 2)}%`} 
          color="orange" 
          icon={<Percent className="w-4 h-4"/>}
        />

        {formData.hasExpenses && (
          <ResultItem 
            label="Расходы" 
            value={formatCurrency(results.expenses)} 
            color="gray"
          />
        )}
        
        <ResultItem 
          label="Налоговая база" 
          value={formatCurrency(results.taxableIncome)} 
        />
        
        {results.deduction > 0 && (
          <ResultItem 
            label="Налоговый вычет" 
            value={formatCurrency(results.deduction)} 
            color="green" 
          />
        )}

        {formData.fiscalRole === 'entrepreneur' && (
          <ResultItem 
            label="Фиксированные страховые взносы" 
            value={formatCurrency(results.fixedInsuranceFee)} 
            color="orange" 
          />
        )}
        
        {formData.includeInsurance && (
          <ResultItem 
            label="Добровольные страховые взносы" 
            value={formatCurrency(results.voluntaryInsurance)} 
            color="green" 
            icon={<PiggyBank className="w-4 h-4"/>} 
          />
        )}
        
        <ResultItem 
          label="Чистый доход" 
          value={formatCurrency(results.netIncome)} 
          color="green" 
          icon={<Wallet className="w-4 h-4"/>} 
        />

        {/* Информация о предельном доходе */}
        {results.taxSystem === 'npd' && (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <AlertCircle className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Лимит доходов для самозанятых</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Годовой доход самозанятого не должен превышать <span className="font-medium text-amber-600 dark:text-amber-400">2,4 млн ₽</span>. 
                  {results.incomeAmount > 2400000 / results.periodMonths * 12 ? 
                    <span className="text-red-600 dark:text-red-400 font-medium"> При текущем доходе вы превысите лимит! Рассмотрите другие налоговые режимы.</span> : 
                    <span className="text-green-600 dark:text-green-400"> При текущем доходе вы не превысите лимит.</span>}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Минимальный налог для УСН Доходы-Расходы */}
        {results.taxSystem === 'usn_profit' && results.isMinTaxApplied && (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <AlertCircle className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Применен минимальный налог</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Для УСН Доходы-Расходы применен минимальный налог <span className="font-medium text-yellow-600 dark:text-yellow-400">1% от дохода</span> ({formatCurrency(results.minTax)}), 
                  так как он превышает расчетный налог по ставке 15%.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Сравнение налоговых систем */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Сравнение налоговых режимов</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Налоговый режим
                </th>
                <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Налог
                </th>
                <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Взносы
                </th>
                <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Общая нагрузка
                </th>
                <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Эффект. ставка
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
              {Object.entries(results.comparison).map(([system, data]) => {
                const isCurrentSystem = formData.taxSystem === system;
                return (
                  <tr key={system} className={isCurrentSystem ? "bg-indigo-50 dark:bg-indigo-900/20" : ""}>
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-200">
                      {isCurrentSystem && <span className="text-indigo-600 dark:text-indigo-400">▶ </span>}
                      {system === 'npd' ? 'Самозанятость (НПД)' : 
                       system === 'usn_income' ? 'УСН Доходы (6%)' : 
                       'УСН Доходы-Расходы (15%)'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-right">
                      {formatCurrency(data.taxAmount)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-right">
                      {formatCurrency(data.insuranceFee)}
                    </td>
                    <td className={`px-3 py-2 whitespace-nowrap text-sm font-medium text-right ${isCurrentSystem ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-900 dark:text-gray-200'}`}>
                      {formatCurrency(data.totalBurden)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-right">
                      {formatNumber(data.effectiveRate, 1)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          Оптимальный налоговый режим для вас: 
          <span className="font-medium text-indigo-600 dark:text-indigo-400"> 
            {Object.entries(results.comparison).sort((a, b) => a[1].totalBurden - b[1].totalBurden)[0][0] === 'npd' ? 'Самозанятость (НПД)' : 
             Object.entries(results.comparison).sort((a, b) => a[1].totalBurden - b[1].totalBurden)[0][0] === 'usn_income' ? 'УСН Доходы (6%)' : 
             'УСН Доходы-Расходы (15%)'}
          </span>
        </p>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;